import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import strings from '../../../utils/Strings';
import AuthClient from '../data/api/AuthClient';
import Loader from '../../../shared/components/Loader';
import Alert, { AlertType } from '../../../shared/components/Alert';
import { useAuthContext } from '../data/context/AuthContext';

const LoginComponent: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { dispatch } = useAuthContext();
  //alert types for error and success messages to be shown to the user
  const [alertConfig, setAlertConfig] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: AlertType;
    primaryButtonText?: string;
    onPrimaryClick?: () => void;
    secondaryButtonText?: string;
    onSecondaryClick?: () => void;
  }>({ show: false, title: '', message: '', type: 'info' });

  const closeAlert = () => setAlertConfig(prev => ({ ...prev, show: false }));

  const storeSession = (tokenResponse: any, emailAddr: string, pass: string) => {
    const token = tokenResponse.access_token;
    const region = tokenResponse.region || 'ap-south-1';
    localStorage.setItem('iqadmin_token', token);
    localStorage.setItem('iqadmin_region', region);
    localStorage.setItem('iqadmin_email', emailAddr);
    localStorage.setItem('iqadmin_password', pass);
    dispatch({
      type: 'SET_TOKEN',
      payload: { token, region, email: emailAddr },
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await AuthClient.login({
        email_address: email.trim().toLowerCase(),
        entity_password: password,
        entity_type: 'INTERVIEWIQ_ADMIN',
      });

      if (res.error) {
        setAlertConfig({
          show: true,
          title: 'Login Failed',
          message: res.error,
          type: 'error',
        });
        return;
      }

      const tokenResponse = res as any;

      storeSession(tokenResponse, email.trim().toLowerCase(), password);
      navigate('/dashboard');
    } catch (err: any) {
      console.log("ERROR", err)
      const msg = err?.response?.data?.error || 'Failed to connect to the server. Please try again later.';
      setAlertConfig({
        show: true,
        title: 'Connection Error',
        message: msg,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Loader isLoading={isLoading} message="Signing in..." />
      <Alert
        show={alertConfig.show}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={closeAlert}
        primaryButtonText={alertConfig.primaryButtonText}
        onPrimaryClick={alertConfig.onPrimaryClick}
        secondaryButtonText={alertConfig.secondaryButtonText}
        onSecondaryClick={alertConfig.onSecondaryClick}
      />
      <motion.div
        className="login-form-wrapper"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="brand-header" style={{ justifyContent: 'center' }}>
          <img src="/logo_login_name.png" alt="InterviewIQ Logo" className="brand-logo-img" />
          {/* <span className="brand-text" style={{ fontSize: '30px' }}>
            Joz <span style={{ color: 'var(--iq-landing-primary)', fontSize: '30px' }}>HRM</span>
          </span> */}
        </div>

        {/* <div className="portal-label">{strings.login.portalText}</div> */}
        <h2 className="welcome-title">{strings.login.welcomeBack}</h2>
        <p className="welcome-subtitle">{strings.login.subtitle}</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group border-input-group">
            <label className="field-label">Email Address</label>
            <input
              type="email"
              className="form-input-line"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group border-input-group">
            <label className="field-label">Password</label>
            <div className="password-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input-line password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="password-toggle-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-actions">
            <Link to="/forgot-password" className="forgot-link">{strings.login.forgotPassword}</Link>
          </div>

          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? 'Signing in...' : <>{strings.login.signInButton} <ArrowRight size={18} /></>}
          </button>
        </form>

      </motion.div>
    </>
  );
};

export default LoginComponent;
